import { Link, Navigate, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { GroupTypeBadge } from '@/components/ui/group-type-badge'
import { Header, Footer } from '@/components/layout'
import { useDocumentHead, DEFAULT_DESCRIPTION } from '@/hooks/useDocumentHead'
import { useAuth } from '@/auth'
import { useProfile } from '@/hooks/useProfile'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { groupService } from '@/services/groupService'
import type { Group } from '@/services/groupService'
import { Trophy, Users, Calendar, TrendingUp, Sparkles, ChevronRight, Zap, Shield, ArrowRight, Building2, ClipboardList, Target, Bell, BarChart3, UserPlus } from 'lucide-react'

export function Home() {
  const { isAuthenticated, login } = useAuth()
  const { needsCreate, isLoading: profileLoading } = useProfile()
  const location = useLocation()
  
  // Featured groups state
  const [featuredGroups, setFeaturedGroups] = useState<Group[]>([])
  const [featuredLoading, setFeaturedLoading] = useState(true)
  const [skeletonFading, setSkeletonFading] = useState(false)

  // Helper to get stagger class
  const getStaggerClass = (index: number) => {
    const staggerIndex = Math.min(index + 1, 6)
    return `stagger-${staggerIndex}`
  }
  
  // Fetch featured groups
  useEffect(() => {
    async function fetchFeaturedGroups() {
      try {
        const groups = await groupService.listFeatured()
        // Start fade-out animation for skeleton and fade-in for content simultaneously
        setSkeletonFading(true)
        setFeaturedGroups(groups)
        // After fade completes, hide skeleton completely
        setTimeout(() => {
          setFeaturedLoading(false)
          setSkeletonFading(false)
        }, 300) // Match transition duration
      } catch (err) {
        console.error('Failed to load featured groups:', err)
        setSkeletonFading(true)
        setTimeout(() => {
          setFeaturedLoading(false)
          setSkeletonFading(false)
        }, 300)
      }
    }
    fetchFeaturedGroups()
  }, [])

  // Scroll animations
  const heroAnimation = useScrollAnimation({ threshold: 0.2 })
  const featuredAnimation = useScrollAnimation({ threshold: 0.1 })
  const forWhoAnimation = useScrollAnimation({ threshold: 0.1 })
  const howItWorksAnimation = useScrollAnimation({ threshold: 0.1 })
  const featuresAnimation = useScrollAnimation({ threshold: 0.1 })
  const ctaAnimation = useScrollAnimation({ threshold: 0.2 })
  
  // Home page uses the default app title
  useDocumentHead({
    fullTitle: 'Badminton Leagues - Track Rankings & Compete',
    description: DEFAULT_DESCRIPTION,
  })

  // Redirect authenticated users who need to complete profile setup
  if (isAuthenticated && !profileLoading && needsCreate) {
    return <Navigate to="/signup" state={{ from: location }} replace />
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Gradient overlays */}
          <div 
            className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl"
            style={{ animation: 'float 15s ease-in-out infinite' }}
          />
          <div 
            className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"
            style={{ animation: 'float 18s ease-in-out infinite reverse' }}
          />
          
          {/* Animated SVG Background Layer */}
          <svg 
            className="absolute inset-0 w-full h-full opacity-30 dark:opacity-20"
            viewBox="0 0 1200 600"
            preserveAspectRatio="xMidYMid slice"
          >
            {/* Animated poly lines creating depth */}
            <g className="opacity-40">
              <path
                d="M 0,200 Q 300,150 600,200 T 1200,200"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="500"
                style={{
                  animation: 'polylineDraw 8s ease-in-out infinite',
                  animationDelay: '0s',
                }}
              />
              <path
                d="M 0,300 Q 300,250 600,300 T 1200,300"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeDasharray="500"
                style={{
                  animation: 'polylineDraw 10s ease-in-out infinite',
                  animationDelay: '2s',
                }}
              />
              <path
                d="M 0,400 Q 300,350 600,400 T 1200,400"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                strokeDasharray="500"
                style={{
                  animation: 'polylineDraw 12s ease-in-out infinite',
                  animationDelay: '4s',
                }}
              />
              {/* Diagonal lines for depth */}
              <line
                x1="0"
                y1="0"
                x2="1200"
                y2="600"
                stroke="currentColor"
                strokeWidth="1"
                opacity="0.2"
                style={{
                  animation: 'courtLine 6s ease-in-out infinite',
                }}
              />
              <line
                x1="1200"
                y1="0"
                x2="0"
                y2="600"
                stroke="currentColor"
                strokeWidth="1"
                opacity="0.15"
                style={{
                  animation: 'courtLine 8s ease-in-out infinite',
                  animationDelay: '1s',
                }}
              />
            </g>
            
            {/* Floating shuttlecocks */}
            <g className="opacity-50">
              {/* Shuttlecock 1 */}
              <g
                style={{
                  animation: 'shuttlecockFloat 20s ease-in-out infinite',
                  transformOrigin: 'center',
                }}
              >
                <circle cx="200" cy="150" r="8" fill="currentColor" opacity="0.4" />
                <path
                  d="M 200,150 L 195,165 L 205,165 Z"
                  fill="currentColor"
                  opacity="0.3"
                />
                <line x1="200" y1="150" x2="200" y2="140" stroke="currentColor" strokeWidth="2" opacity="0.4" />
              </g>
              
              {/* Shuttlecock 2 */}
              <g
                style={{
                  animation: 'shuttlecockFloat 25s ease-in-out infinite',
                  animationDelay: '3s',
                  transformOrigin: 'center',
                }}
              >
                <circle cx="800" cy="250" r="6" fill="currentColor" opacity="0.35" />
                <path
                  d="M 800,250 L 796,260 L 804,260 Z"
                  fill="currentColor"
                  opacity="0.25"
                />
                <line x1="800" y1="250" x2="800" y2="243" stroke="currentColor" strokeWidth="1.5" opacity="0.35" />
              </g>
              
              {/* Shuttlecock 3 */}
              <g
                style={{
                  animation: 'shuttlecockFloat 18s ease-in-out infinite',
                  animationDelay: '6s',
                  transformOrigin: 'center',
                }}
              >
                <circle cx="1000" cy="100" r="7" fill="currentColor" opacity="0.4" />
                <path
                  d="M 1000,100 L 996,112 L 1004,112 Z"
                  fill="currentColor"
                  opacity="0.3"
                />
                <line x1="1000" y1="100" x2="1000" y2="92" stroke="currentColor" strokeWidth="1.8" opacity="0.4" />
              </g>
              
              {/* Shuttlecock 4 */}
              <g
                style={{
                  animation: 'shuttlecockFloat 22s ease-in-out infinite',
                  animationDelay: '9s',
                  transformOrigin: 'center',
                }}
              >
                <circle cx="150" cy="450" r="9" fill="currentColor" opacity="0.35" />
                <path
                  d="M 150,450 L 144,465 L 156,465 Z"
                  fill="currentColor"
                  opacity="0.25"
                />
                <line x1="150" y1="450" x2="150" y2="440" stroke="currentColor" strokeWidth="2" opacity="0.35" />
              </g>
              
              {/* Shuttlecock 5 */}
              <g
                style={{
                  animation: 'shuttlecockFloat 24s ease-in-out infinite',
                  animationDelay: '12s',
                  transformOrigin: 'center',
                }}
              >
                <circle cx="950" cy="500" r="7" fill="currentColor" opacity="0.4" />
                <path
                  d="M 950,500 L 945,513 L 955,513 Z"
                  fill="currentColor"
                  opacity="0.3"
                />
                <line x1="950" y1="500" x2="950" y2="492" stroke="currentColor" strokeWidth="1.8" opacity="0.4" />
              </g>
            </g>
            
            {/* Subtle horizontal lines for depth */}
            <g className="opacity-15" style={{ animation: 'depthPulse 10s ease-in-out infinite' }}>
              <rect x="0" y="250" width="1200" height="1" fill="currentColor" />
              <rect x="0" y="350" width="1200" height="1" fill="currentColor" />
            </g>
          </svg>
          
          {/* Fade out at bottom */}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background to-transparent z-10" />
        </div>
        
        <div className="container mx-auto px-4 py-12 md:py-20 relative z-10">
          <div 
            ref={heroAnimation.ref}
            className={`flex flex-col items-center text-center max-w-3xl mx-auto ${
              heroAnimation.isVisible ? 'animate-fade-in-up' : ''
            }`}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-6">
              <Trophy className="h-3.5 w-3.5" />
              <span>Badminton Leagues, Simplified</span>
            </div>
            
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl lg:text-6xl">
            Your League, 
              <span className="text-primary">  Run Beautifully</span>
            </h1>
            <p className="mt-6 text-base text-muted-foreground md:text-lg max-w-2xl leading-relaxed">
              Run seasons. Track rankings. Enjoy the game.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              {isAuthenticated ? (
                <>
                  <Button size="lg" className="px-8 gap-2" asChild>
                    <Link to="/groups">
                      Go to My Groups
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </>
              ) : (
                <>
                  <Button size="lg" className="px-8 gap-2" onClick={() => login()}>
                    Get Started
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button size="lg" variant="outline" className="px-6" asChild>
                    <Link to="/groups">Explore Groups</Link>
                  </Button>
                </>
              )}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Free to start • Set up in minutes
            </p>
          </div>
        </div>
      </section>

      {/* Featured Groups Section - Show social proof early */}
      {(featuredLoading || featuredGroups.length > 0) && (
        <section className="relative border-y bg-muted/20">
          <div 
            ref={featuredAnimation.ref}
            className={`container mx-auto px-4 pt-10 md:pt-14 pb-6 md:pb-8 ${
              featuredAnimation.isVisible ? 'animate-fade-in-up' : ''
            }`}
          >
            <div className="flex items-center gap-2.5 mb-6">
              <div className="h-8 w-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-purple-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Featured Groups</h2>
              </div>
            </div>
            
            <div className="relative">
              {/* Skeleton loader - fades out when data loads */}
              {(featuredLoading || skeletonFading) && (
                <div className={`grid gap-3 sm:grid-cols-2 lg:grid-cols-3 transition-opacity duration-300 ease-out ${
                  skeletonFading ? 'opacity-0 pointer-events-none' : 'opacity-100'
                }`}>
                  {[1, 2, 3].map((i) => (
                    <Card key={i} className="overflow-hidden">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-12 w-12 rounded-lg" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-4 w-28" />
                            <Skeleton className="h-3 w-20" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              {/* Actual content - fades in as skeleton fades out */}
              {featuredGroups.length > 0 && (
                <div className={`grid gap-3 sm:grid-cols-2 lg:grid-cols-3 transition-opacity duration-300 ease-out ${
                  skeletonFading || featuredLoading ? 'opacity-0' : 'opacity-100'
                }`}>
                  {featuredGroups.map((group) => (
                    <Link key={group.id} to={`/group/${group.id}`}>
                      <Card className="group overflow-hidden hover:border-purple-500/40 transition-colors h-full">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center text-2xl overflow-hidden shrink-0">
                              {group.icon?.startsWith('data:') || group.icon?.startsWith('https://') ? (
                                <img src={group.icon} alt={group.name} className="h-full w-full object-cover" />
                              ) : (
                                group.icon || '🏸'
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <h3 className="font-medium text-sm truncate group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                                  {group.name}
                                </h3>
                                <GroupTypeBadge type={group.type} />
                              </div>
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                <span>{group.memberCount} member{group.memberCount !== 1 ? 's' : ''}</span>
                              </p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-purple-500 transition-colors shrink-0" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* For Who Section */}
      <section className="border-b bg-muted/30">
        <div 
          ref={forWhoAnimation.ref}
          className={`container mx-auto px-4 py-12 md:py-16 ${
            forWhoAnimation.isVisible ? 'animate-fade-in-up' : ''
          }`}
        >
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-semibold mb-2">
              Designed for Every Part of Your League
            </h2>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">
              From group setup to match day and beyond
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {/* Group Setup & Growth */}
            <Card className={`relative overflow-hidden group hover:border-primary/40 transition-colors ${
              forWhoAnimation.isVisible ? `animate-fade-in-up ${getStaggerClass(0)}` : ''
            }`}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
              <CardContent className="p-6 relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg leading-tight">
                    Group Setup &amp; Growth
                  </h3>
                </div>

                <p className="text-sm text-muted-foreground mb-4">
                  Build your group with tools that make it easy to manage members and welcome new players.
                </p>

                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <Shield className="h-4 w-4 text-primary shrink-0" />
                    <span>Full admin controls and member management</span>
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <UserPlus className="h-4 w-4 text-primary shrink-0" />
                    <span>Public group page to attract new members</span>
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <BarChart3 className="h-4 w-4 text-primary shrink-0" />
                    <span>Automatic ELO-based group rankings</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Running the League */}
            <Card className={`relative overflow-hidden group hover:border-amber-500/40 transition-colors ${
              forWhoAnimation.isVisible ? `animate-fade-in-up ${getStaggerClass(1)}` : ''
            }`}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
              <CardContent className="p-6 relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <ClipboardList className="h-5 w-5 text-amber-500" />
                  </div>
                  <h3 className="font-semibold text-lg leading-tight">
                    Running the League
                  </h3>
                </div>

                <p className="text-sm text-muted-foreground mb-4">
                  Run seasons that feel fair, smooth, and well-structured — without the admin overhead.
                </p>

                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-4 w-4 text-amber-500 shrink-0" />
                    <span>Seasonal leagues with automatic rankings</span>
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4 text-amber-500 shrink-0" />
                    <span>Multiple brackets per season</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Zap className="h-4 w-4 text-amber-500 shrink-0" />
                    <span>Built-in score approval workflow</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Playing & Progress */}
            <Card className={`relative overflow-hidden group hover:border-green-500/40 transition-colors ${
              forWhoAnimation.isVisible ? `animate-fade-in-up ${getStaggerClass(2)}` : ''
            }`}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
              <CardContent className="p-6 relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                    <Target className="h-5 w-5 text-green-500" />
                  </div>
                  <h3 className="font-semibold text-lg leading-tight">
                    Playing &amp; Progress
                  </h3>
                </div>

                <p className="text-sm text-muted-foreground mb-4">
                  Focus on playing while rankings, results, and stats update automatically.
                </p>

                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <Trophy className="h-4 w-4 text-green-500 shrink-0" />
                    <span>Live standings and personal position</span>
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <Bell className="h-4 w-4 text-green-500 shrink-0" />
                    <span>Notifications for score confirmations</span>
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <TrendingUp className="h-4 w-4 text-green-500 shrink-0" />
                    <span>Match history and performance stats</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section 
        ref={howItWorksAnimation.ref}
        className={`container mx-auto px-4 py-12 md:py-16 ${
          howItWorksAnimation.isVisible ? 'animate-fade-in' : ''
        }`}
      >
        <div className="text-center mb-10">
          <h2 className="text-xl md:text-2xl font-semibold mb-2">Up and Running in Minutes</h2>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto">
            Setting up your league is quick and easy
          </p>
        </div>
        
        <div className="grid gap-8 md:grid-cols-4 max-w-4xl mx-auto">
          {[
            { step: '1', title: 'Create Your Group', desc: 'Set up your group profile and invite members' },
            { step: '2', title: 'Add a League', desc: 'Create singles or doubles leagues' },
            { step: '3', title: 'Start a Season', desc: 'Open registrations and assign players to brackets' },
            { step: '4', title: 'Play & Track', desc: 'Players record matches, rankings update automatically' },
          ].map((item, i) => (
            <div 
              key={i} 
              className={`relative flex flex-col items-center text-center ${
                howItWorksAnimation.isVisible ? `animate-fade-in-up ${getStaggerClass(i)}` : ''
              }`}
            >
              <div className="h-12 w-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold mb-4">
                {item.step}
              </div>
              <h3 className="font-semibold mb-1">{item.title}</h3>
              <p className="text-sm text-muted-foreground">{item.desc}</p>
              {i < 3 && (
                <ArrowRight className="hidden md:block absolute -right-4 top-6 h-5 w-5 text-muted-foreground/30" />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="border-t bg-muted/20">
        <div 
          ref={featuresAnimation.ref}
          className={`container mx-auto px-4 py-12 md:py-16 ${
            featuresAnimation.isVisible ? 'animate-fade-in' : ''
          }`}
        >
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-semibold mb-2">Packed with Features</h2>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">
              Everything you need to run successful league competitions
            </p>
          </div>
          
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Trophy, title: 'Points-Based Rankings', desc: 'Win 3 points per match, standings update instantly after approval', color: 'text-amber-500' },
              { icon: Calendar, title: 'Seasonal Competitions', desc: 'Fresh starts each season with configurable brackets and levels', color: 'text-green-500' },
              { icon: Users, title: 'Multiple Brackets', desc: 'Separate players by skill level: Beginner, Intermediate, Advanced', color: 'text-blue-500' },
              { icon: Shield, title: 'Score Approval', desc: 'Both players confirm results to prevent disputes', color: 'text-rose-500' },
              { icon: Bell, title: 'Push Notifications', desc: 'Never miss a score submission or challenge', color: 'text-purple-500' },
              { icon: TrendingUp, title: 'ELO Ratings', desc: 'Group-wide skill tracking beyond seasonal leagues', color: 'text-orange-500' },
            ].map((feature, i) => (
              <Card 
                key={i} 
                className={`group hover:border-primary/30 transition-colors ${
                  featuresAnimation.isVisible ? `animate-fade-in-up ${getStaggerClass(i)}` : ''
                }`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <feature.icon className={`h-5 w-5 ${feature.color}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{feature.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {feature.desc}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {!isAuthenticated && (
        <section 
          ref={ctaAnimation.ref}
          className={`container mx-auto px-4 py-12 md:py-16 ${
            ctaAnimation.isVisible ? 'animate-scale-in' : ''
          }`}
        >
          <Card className="bg-gradient-to-br from-primary/10 via-purple-500/5 to-pink-500/5 border-primary/20">
            <CardContent className="p-8 md:p-12 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
                <Sparkles className="h-3.5 w-3.5" />
                <span>Get Started Today</span>
              </div>
              <h2 className="text-xl md:text-2xl font-bold mb-3">
                Ready to Transform Your Group?
              </h2>
              <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                Join groups already using Badminton Leagues to run engaging competitions. 
                Set up takes just a few minutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button size="lg" className="px-8 gap-2" onClick={() => login()}>
                  Create Your Group
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" className="px-6" asChild>
                  <Link to="/groups">Join an Existing Group</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      <Footer />
    </div>
  )
}
